import type {ChronologicalCommentsSort} from '@github-ui/commenting/ChronologicalCommentsSort'
import type {IssueViewerQueryIdentifier} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useSuspenseQuery} from '@github-ui/react-query'
import {type ReactNode, type RefObject, useMemo, useState} from 'react'

import {getInitialTimelinePageSize} from '../../constants/values'
import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import {useIssueHeaderDataContext} from '../header/IssueHeaderDataContext'
import type {IssueNode} from '../issue-viewer-page-query-types'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import {type TimelineFeed, type TimelineItemTypes, timelineQueryOptions} from './fetch-timeline-items'
import type {EventProps, IssueTimelineItemFragment} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {frontParamsFor} from './timeline-prefill'
import {orderTimelineItems} from './timeline-sort'
import {TimelinePanel} from './TimelinePanel'
import {type UseTimelineItemsResult, useTimelineItemsTanstack} from './use-timeline-items-tanstack'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
  newestFirst?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItemFragment>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

export type IssueTimelineSecondaryFragment = Pick<IssueSecondaryNode, 'isTransferInProgress'>

export type IssueTimelineData = Pick<IssueNode, 'id' | 'url'> & {
  readonly repository: Pick<IssueNode['repository'], 'id' | 'nameWithOwner'>
}

export type IssueTimelineRenderProps = {
  /** Cursor the newest window pins to, derived from the page payload by `timeline-prefill`. */
  initialBackAnchor?: string
  issueBodyRef?: RefObject<HTMLDivElement | null>
  commentsHeadingId?: string
  issueSecondary?: IssueTimelineSecondaryFragment
  highlightedEvent: string | undefined
  page?: number
  sort?: ChronologicalCommentsSort
  // Passed rather than selected because the caller already holds all three.
  owner: string
  repo: string
  number: number
  feed?: TimelineFeed
  /** Activity feed only. Must be referentially stable; it keys the timeline's queries. */
  itemTypes?: TimelineItemTypes
  emptyState?: ReactNode
  /** Shown in place of `emptyState` until the timeline is first read. */
  loadingState?: ReactNode
  primaryQueryKey?: readonly unknown[]
  primaryIdentifier?: IssueViewerQueryIdentifier
  /** Opens only the bounded highlighted window, without reading either end of Activity. */
  focusedOnly?: boolean
} & EventProps

type TanStackIssueTimelineProps = IssueTimelineRenderProps & {
  timelineData: IssueTimelineData
}

export const TanStackIssueTimeline = ({timelineData, ...props}: TanStackIssueTimelineProps) => (
  <IssueTimelineContent {...props} data={timelineData} />
)

function IssueTimelineContent({
  data,
  initialBackAnchor,
  issueBodyRef,
  commentsHeadingId,
  issueSecondary: _issueSecondary,
  viewer,
  highlightedEvent,
  page,
  sort = 'oldest-first',
  owner,
  repo,
  number,
  feed = 'activity',
  itemTypes,
  emptyState,
  loadingState,
  primaryQueryKey,
  primaryIdentifier,
  focusedOnly = false,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  onCommentSeeInTimeline,
  onCommentOpenInConversation,
  commentJumpActivationId,
  optionConfig,
  issueUpdatesChannel,
}: IssueTimelineRenderProps & {data: IssueTimelineData}) {
  // The header endpoint is the only thing that refreshes on a transfer, so it outranks the
  // secondary query, which no longer hears about one.
  const sharedHeader = useIssueHeaderDataContext()
  const timelineProps: TanstackTimelineProps = {
    data,
    issueBodyRef,
    commentsHeadingId,
    owner,
    repo,
    number,
    feed,
    primaryQueryKey,
    primaryIdentifier,
    highlightedEvent,
    page,
    sort,
    emptyState,
    loadingState,
    isTransferInProgress: sharedHeader.isTransferInProgress,
    viewer,
    onCommentChange,
    onCommentReply,
    onCommentEditCancel,
    onCommentSeeInTimeline,
    onCommentOpenInConversation,
    commentJumpActivationId,
    optionConfig,
    issueUpdatesChannel,
    focusedOnly,
  }

  return itemTypes ? (
    // Keyed by filter too: loaded pages and the discovered anchor belong to one filtered timeline.
    <FilteredTimelineFirstPage
      key={`${feed}:${itemTypes.join(',')}`}
      owner={owner}
      repo={repo}
      number={number}
      feed={feed}
      itemTypes={itemTypes}
    >
      <TanstackTimeline {...timelineProps} feed={feed} itemTypes={itemTypes} initialBackAnchor={initialBackAnchor} />
    </FilteredTimelineFirstPage>
  ) : (
    <TanstackTimeline key={feed} {...timelineProps} feed={feed} initialBackAnchor={initialBackAnchor} />
  )
}

// Suspends until a filter's first page loads, so its empty state doesn't show while loading.
function FilteredTimelineFirstPage({
  owner,
  repo,
  number,
  feed,
  itemTypes,
  children,
}: {
  owner: string
  repo: string
  number: number
  feed: TimelineFeed
  itemTypes: TimelineItemTypes
  children: ReactNode
}) {
  const {queryKey, queryFn} = timelineQueryOptions(
    owner,
    repo,
    number,
    frontParamsFor(getInitialTimelinePageSize()),
    feed,
    itemTypes,
  )
  useSuspenseQuery({queryKey, queryFn})

  return children
}

type TimelineSourceProps = {
  data: IssueTimelineData
  issueBodyRef?: RefObject<HTMLDivElement | null>
  commentsHeadingId?: string
  owner: string
  repo: string
  number: number
  feed: TimelineFeed
  highlightedEvent: string | undefined
  page?: number
  sort: ChronologicalCommentsSort
  emptyState?: ReactNode
  loadingState?: ReactNode
  isTransferInProgress?: boolean
} & EventProps
type TanstackTimelineProps = TimelineSourceProps & {
  itemTypes?: TimelineItemTypes
  initialBackAnchor?: string
  primaryQueryKey?: readonly unknown[]
  primaryIdentifier?: IssueViewerQueryIdentifier
  focusedOnly?: boolean
}

const TanstackTimeline = (props: TanstackTimelineProps) => {
  const [retry, setRetry] = useState({highlightedEvent: props.highlightedEvent, activation: 0})
  if (retry.highlightedEvent !== props.highlightedEvent) {
    setRetry({highlightedEvent: props.highlightedEvent, activation: 0})
  }
  const retryActivation = retry.highlightedEvent === props.highlightedEvent ? retry.activation : 0
  const commentJumpActivationId =
    props.commentJumpActivationId ?? (retryActivation > 0 ? `retry:${props.highlightedEvent}` : undefined)
  const primaryIdentifier = props.primaryIdentifier ?? {owner: props.owner, repo: props.repo, number: props.number}
  const result = useTimelineItemsTanstack({
    owner: props.owner,
    repo: props.repo,
    number: props.number,
    issueId: props.data.id,
    feed: props.feed,
    itemTypes: props.itemTypes,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
    initialBackAnchor: props.initialBackAnchor,
    primaryQueryKey: props.primaryQueryKey,
    primaryIdentifier,
    focusedOnly: props.focusedOnly,
  })

  return (
    <TimelineItemsPanel
      {...props}
      {...result}
      commentJumpActivationId={
        commentJumpActivationId === undefined ? undefined : `${commentJumpActivationId}:${retryActivation}`
      }
      retryCommentNavigation={() => {
        setRetry(previous => ({highlightedEvent: props.highlightedEvent, activation: previous.activation + 1}))
        result.retryCommentNavigation?.()
      }}
    />
  )
}

type TimelineItemsPanelProps = TimelineSourceProps & UseTimelineItemsResult

const TimelineItemsPanel = ({
  data,
  viewer,
  highlightedEvent,
  page,
  sort,
  emptyState,
  loadingState,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  onCommentSeeInTimeline,
  onCommentOpenInConversation,
  commentJumpActivationId,
  optionConfig,
  isTransferInProgress,
  issueBodyRef,
  commentsHeadingId,
  timelineItems,
  totalItemCount,
  isLoading,
  highlightedReady,
  newestSectionQueryKey,
  onCanonicalTimelineUpdated,
  commentNavigationState,
  retryCommentNavigation,
  issueUpdatesChannel,
  owner,
  repo,
  number,
  feed,
}: TimelineItemsPanelProps) => {
  const orderedTimelineItems = useMemo(
    () => (timelineItems ? orderTimelineItems(timelineItems, sort) : timelineItems),
    [timelineItems, sort],
  )

  return (
    <TimelinePanel
      timelineItems={orderedTimelineItems}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
      newestSectionQueryKey={newestSectionQueryKey}
      onCanonicalTimelineUpdated={onCanonicalTimelineUpdated}
      commentNavigationState={commentNavigationState}
      retryCommentNavigation={retryCommentNavigation}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={data.id}
      owner={owner}
      repo={repo}
      number={number}
      feed={feed}
      repositoryId={data.repository.id}
      repositoryNameWithOwner={data.repository.nameWithOwner}
      issueUrl={data.url}
      page={page}
      isTransferInProgress={isTransferInProgress}
      viewer={viewer}
      issueBodyRef={issueBodyRef}
      commentsHeadingId={commentsHeadingId}
      emptyState={emptyState}
      loadingState={loadingState}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      onCommentSeeInTimeline={onCommentSeeInTimeline}
      onCommentOpenInConversation={onCommentOpenInConversation}
      commentJumpActivationId={commentJumpActivationId}
      optionConfig={optionConfig}
      issueUpdatesChannel={issueUpdatesChannel}
    />
  )
}
